/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck - Material Tailwind component prop warnings
import React, { FC } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
} from '@material-tailwind/react';
import AppToggle from '@/components/atoms/AppToggle';
import { Text, Caption } from '@/components/atoms/AppText';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { LuSettings2 } from 'react-icons/lu';
import { ELEMENTS_PROPS } from '@/constant/global/global.constant';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';

interface ConfigAccordionProps {
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
  title: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  iconColor: string;
  titleColor?: string;
  children: React.ReactNode;
  switchColor?: 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'teal';
  /** Alias for switchColor — accepted from section files */
  customColor?: 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'teal';
  customIcon?: React.ReactNode;
  hasError?: boolean;
  showToggle?: boolean;
  /** data-testid for the accordion header */
  testId?: string;
}

const ConfigAccordion: FC<ConfigAccordionProps> = ({
  index,
  isOpen,
  onToggle,
  title,
  enabled,
  onEnabledChange,
  iconColor,
  titleColor,
  children,
  switchColor,
  customColor,
  customIcon,
  hasError,
  showToggle = true,
  testId,
}) => {
  const resolvedSwitchColor = customColor ?? switchColor;
  return (
    <div className={`group border-b ${APP_BORDER.soft}`}>
      <Accordion
        open={isOpen}
        icon={
          <div
            className={`transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`}
          >
            <ChevronDownIcon className={`h-5 w-5 ${APP_TEXT.soft}`} />
          </div>
        }
      >
        <AccordionHeader
          onClick={() => onToggle(index)}
          data-testid={testId ? `${testId}-header` : ''}
          className={`relative ${isOpen ? APP_BG.surface : APP_BG.base} px-6 py-4 transition-all duration-300`}
        >
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-primary via-secondary to-tertiary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="flex items-center justify-between w-full pr-4">
            <div className="flex items-center gap-3">
              <div
                className={`relative flex items-center justify-center w-9 h-9 rounded-xl ${iconColor} shadow-sm`}
              >
                <div className="w-5 h-5 text-white">
                  {customIcon || <LuSettings2 className="w-5 h-5" />}
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <Text
                    as="span"
                    size="lg"
                    weight="bold"
                    className={`transition-colors ${isOpen ? titleColor || APP_TEXT.primary : APP_TEXT.default}`}
                  >
                    {title}
                  </Text>
                  {hasError && (
                    <Text
                      as="span"
                      size="xs"
                      weight="bold"
                      color="danger"
                      className="inline-flex items-center gap-1 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 px-1.5 py-0.5 rounded-full"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                      {ELEMENTS_PROPS.CONFIG_ACCORDION.FIX_REQUIRED}
                    </Text>
                  )}
                </div>
                <Caption size="sm" color="muted" className="mt-0.5">
                  {showToggle
                    ? enabled
                      ? ELEMENTS_PROPS.CONFIG_ACCORDION.CONFIG_ENABLED
                      : ELEMENTS_PROPS.CONFIG_ACCORDION.CONFIG_DISABLED
                    : null}
                </Caption>
              </div>
            </div>
            {showToggle && (
              <div onClick={(e) => e.stopPropagation()}>
                <AppToggle
                  checked={enabled}
                  onChange={onEnabledChange}
                  color={resolvedSwitchColor as any}
                  size="sm"
                  testId={`${testId ? `${testId}-toggle` : ''}`}

                />
              </div>
            )}
          </div>
        </AccordionHeader>
        <AccordionBody
          className={`px-8 py-6 border-t ${APP_BORDER.filterRow} ${APP_BG.surface} transition-all duration-300`}
        >
          <div className="space-y-4">{children}</div>
        </AccordionBody>
      </Accordion>
    </div>
  );
};

export default ConfigAccordion;
